import React from 'react';

/**
 * Delays `cb` until `delay` ms pass without another call, using the latest
 * `cb`. The returned function has a `cancel()` that drops the pending call;
 * it is opt-in, and unmounting does not cancel by itself (the trailing call
 * still happens, which callers that save on exit rely on).
 */
export function useDebouncedCallback<A extends any[], R>(
  cb: (...args: A) => R,
  delay: number
) {
  const cbRef = React.useRef(cb);
  React.useEffect(() => {
    cbRef.current = cb;
  });
  return React.useMemo(() => {
    let isDebounced: number | undefined = undefined;
    const debounced = (...args: Parameters<typeof cb>) => {
      window.clearTimeout(isDebounced);
      isDebounced = window.setTimeout(() => cbRef.current(...args), delay);
    };
    debounced.cancel = () => window.clearTimeout(isDebounced);
    return debounced;
  }, [delay]);
}
